import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortal, Venue, Location, CreateVenueRequest, CreateLocationRequest } from '../../context/PortalContext';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import QRCodeDisplay, { QRCodeButton, QRCodeContent } from '../../components/QRCodeDisplay/QRCodeDisplay';
import VenueActionsMenu from '../../components/VenueActionsMenu/VenueActionsMenu';
import LocationActionsMenu from '../../components/LocationActionsMenu/LocationActionsMenu';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import Toast from '../../components/Toast/Toast';
import './DeveloperPortalLayout.css';
import './CSVUploadModal.css';

interface VenuesProps {
  onNavigate: (section: string) => void;
}

const Venues: React.FC<VenuesProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { 
    venues, 
    locations, 
    isLoading, 
    error, 
    loadVenues, 
    createVenue, 
    updateVenue,
    deleteVenue,
    createLocation, 
    updateLocation,
    deleteLocation,
    getLocationsForVenue,
    clearError,
    validateCSV,
    bulkVenues
  } = usePortal();
  
  const [expandedVenues, setExpandedVenues] = useState<Set<string>>(new Set());
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [showCreateLocation, setShowCreateLocation] = useState<string | null>(null);
  const [showEditVenue, setShowEditVenue] = useState<Venue | null>(null);
  const [showEditLocation, setShowEditLocation] = useState<Location | null>(null);
  const [showDeleteVenueDialog, setShowDeleteVenueDialog] = useState<Venue | null>(null);
  const [showDeleteLocationDialog, setShowDeleteLocationDialog] = useState<Location | null>(null);
  const [expandedQRVenues, setExpandedQRVenues] = useState<Set<string>>(new Set());
  const [expandedQRLocations, setExpandedQRLocations] = useState<Set<string>>(new Set());
  const [copiedVenueId, setCopiedVenueId] = useState<string | null>(null);
  
  // CSV upload state
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvAction, setCsvAction] = useState<'add' | 'replace' | 'delete'>('add');
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [csvSuccess, setCsvSuccess] = useState<string | null>(null);
  const [csvProgress, setCsvProgress] = useState<{current: number, total: number} | null>(null);
  
  // Toast notifications
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);

  // New venue form state
  const [newVenue, setNewVenue] = useState({
    name: '',
    city: '',
    state: '',
    country: '',
    internal_id: ''
  });

  // New location form state
  const [newLocation, setNewLocation] = useState({
    label: ''
  });

  // Edit venue form state
  const [editVenue, setEditVenue] = useState({
    name: '',
    city: '',
    state: '',
    country: '',
    internal_id: ''
  });

  // Edit location form state
  const [editLocation, setEditLocation] = useState({
    label: ''
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const toggleVenueExpansion = (venueId: string) => {
    const newExpanded = new Set(expandedVenues);
    if (newExpanded.has(venueId)) {
      newExpanded.delete(venueId);
    } else {
      newExpanded.add(venueId);
    }
    setExpandedVenues(newExpanded);
  };

  const handleCopyVenueId = async (venueId: string) => {
    try {
      await navigator.clipboard.writeText(venueId);
      setCopiedVenueId(venueId);
      // Reset after 2 seconds
      setTimeout(() => setCopiedVenueId(null), 2000);
    } catch (err) {
      console.error('Failed to copy venue ID:', err);
    }
  };

  const handleCreateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    
    // Clear validation errors
    setValidationErrors({});
    
    const venueData: CreateVenueRequest = {
      ...newVenue,
      internal_id: newVenue.internal_id || null,
      user_id: user.id
    };
    
    const result = await createVenue(venueData);
    if (result) {
      setNewVenue({ name: '', city: '', state: '', country: '', internal_id: '' });
      setShowCreateVenue(false);
    }
  };

  const handleCreateLocation = async (e: React.FormEvent, venueId: string) => {
    e.preventDefault();
    
    const locationData: CreateLocationRequest = {
      ...newLocation,
      venue_id: venueId
    };
    
    const result = await createLocation(locationData);
    if (result) {
      setNewLocation({ label: '' });
      setShowCreateLocation(null);
    }
  };

  const handleEditVenue = (venue: Venue) => {
    setEditVenue({
      name: venue.name,
      city: venue.city,
      state: venue.state,
      country: venue.country,
      internal_id: venue.internal_id || ''
    });
    setShowEditVenue(venue);
  };

  const handleUpdateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showEditVenue) return;
    
    // Clear validation errors
    setValidationErrors({});
    
    const result = await updateVenue(showEditVenue.id, {
      ...editVenue,
      internal_id: editVenue.internal_id || null
    });
    if (result) {
      setShowEditVenue(null);
      setEditVenue({ name: '', city: '', state: '', country: '', internal_id: '' });
    }
  };

  const handleDeleteVenue = (venue: Venue) => {
    setShowDeleteVenueDialog(venue);
  };

  const handleDeleteVenueConfirm = async () => {
    if (showDeleteVenueDialog) {
      try {
        await deleteVenue(showDeleteVenueDialog.id);
        setShowDeleteVenueDialog(null);
        setToast({
          message: `Successfully deleted venue: ${showDeleteVenueDialog.name}`,
          type: 'success'
        });
      } catch (error) {
        setToast({
          message: `Failed to delete venue: ${error}`,
          type: 'error'
        });
      }
    }
  };

  const handleDeleteVenueCancel = () => {
    setShowDeleteVenueDialog(null);
  };

  const handleEditLocation = (location: Location) => {
    setEditLocation({
      label: location.label
    });
    setShowEditLocation(location);
  };

  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showEditLocation) return;
    
    const result = await updateLocation(showEditLocation.id, editLocation);
    if (result) {
      setShowEditLocation(null);
      setEditLocation({ label: '' });
    }
  };

  const handleDeleteLocation = (location: Location) => {
    setShowDeleteLocationDialog(location);
  };

  const handleDeleteLocationConfirm = async () => {
    if (showDeleteLocationDialog) {
      await deleteLocation(showDeleteLocationDialog.id);
      setShowDeleteLocationDialog(null);
    }
  };

  const handleDeleteLocationCancel = () => {
    setShowDeleteLocationDialog(null);
  };

  const toggleVenueQR = (venueId: string) => {
    const newExpanded = new Set(expandedQRVenues);
    if (newExpanded.has(venueId)) {
      newExpanded.delete(venueId);
    } else {
      newExpanded.add(venueId);
    }
    setExpandedQRVenues(newExpanded);
  };

  const toggleLocationQR = (locationId: string) => {
    const newExpanded = new Set(expandedQRLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedQRLocations(newExpanded);
  };

  // CSV handling functions
  const handleCSVFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setCsvError(null);
      parseCSVFile(file);
    }
  };

  const parseCSVFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setCsvError('CSV file must have at least a header row and one data row');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });

        setCsvData(data);
        
        // Validate CSV structure with backend
        try {
          await validateCSV(data);
          setCsvError(null);
        } catch (validationError: any) {
          setCsvError(validationError.message);
        }
      } catch (error) {
        setCsvError('Error parsing CSV file: ' + error);
      }
    };
    reader.readAsText(file);
  };

  const handleCSVUpload = () => {
    setShowCSVUpload(true);
  };

  const handleCSVClose = () => {
    setShowCSVUpload(false);
    setCsvFile(null);
    setCsvData([]);
    setCsvError(null);
    setCsvSuccess(null);
    setCsvProgress(null);
    setCsvAction('add');
    setCsvProcessing(false);
  };

  const handleBulkVenueAction = async () => {
    if (!user?.id || csvData.length === 0) return;
    
    // Show confirmation for replace action
    if (csvAction === 'replace') {
      setShowReplaceConfirm(true);
      return;
    }
    
    await executeBulkAction();
  };

  const executeBulkAction = async () => {
    if (!user?.id || csvData.length === 0) return;
    
    setCsvProcessing(true);
    setCsvError(null);
    setCsvSuccess(null);
    setCsvProgress({ current: 0, total: csvData.length });
    
    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setCsvProgress(prev => {
          if (prev && prev.current < prev.total) {
            return { ...prev, current: prev.current + 1 };
          }
          return prev;
        });
      }, 100);
      
      const result = await bulkVenues(csvData, csvAction);
      
      clearInterval(progressInterval);
      setCsvProgress({ current: csvData.length, total: csvData.length });
      
      if (result.success) {
        // All successful
        setCsvSuccess(`✅ Successfully processed all ${result.processed_count} venues!`);
        setCsvError(null);
        setToast({
          message: `Successfully processed ${result.processed_count} venues!`,
          type: 'success'
        });
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          handleCSVClose();
          loadVenues(); // Refresh the venues list
        }, 2000);
      } else {
        // Partial success - show detailed results
        const errorDetails = result.errors.map((error: any) => 
          `Row ${error.row_number}: ${error.error_message}`
        ).join('\n');
        
        setCsvError(
          `⚠️ Partial success: ${result.success_count} of ${result.processed_count} venues processed successfully.\n\nFailed rows:\n${errorDetails}`
        );
        
        if (result.success_count > 0) {
          setCsvSuccess(`✅ ${result.success_count} venues processed successfully!`);
          setToast({
            message: `Partially successful: ${result.success_count} of ${result.processed_count} venues processed`,
            type: 'info'
          });
        }
        
        // Still refresh venues to show successful ones
        loadVenues();
      }
    } catch (error: any) {
      setCsvError(`❌ Error processing venues: ${error.message}`);
      setCsvSuccess(null);
    } finally {
      setCsvProcessing(false);
      setCsvProgress(null);
    }
  };

  return (
    <div className="venues-container">
      <SectionHeader
        title="Venues & Locations"
        description="Manage your business venues and their locations"
        rightContent={
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="btn-secondary header-action"
              onClick={handleCSVUpload}
            >
              Upload CSV
            </button>
            <button 
              className="btn-primary header-action"
              onClick={() => setShowCreateVenue(true)}
            >
              Create New Venue
            </button>
          </div>
        }
      />

      {/* Info note about Venue ID usage */}
      <div style={{
        padding: '12px 16px',
        marginBottom: '24px',
        backgroundColor: 'var(--color-bg-card)',
        border: '1px solid var(--color-border-light)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'flex-start',
      }}>
        <div>
          <strong style={{ color: 'var(--color-text-primary)', fontSize: '14px' }}>
            About Venue IDs
          </strong>
          <p style={{ 
            color: 'var(--color-text-secondary)', 
            fontSize: '13px', 
            margin: '4px 0 0 0',
            lineHeight: '1.5'
          }}>
            You'll only need the Venue ID for tracking <strong>online visits</strong> via widget integration. 
            For physical visits, customers scan the QR codes generated below.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading venues...</div>
      ) : (
        <>
          {error && (
            <div className="error-message">
              {error}
        </div>
      )}

      {venues.length === 0 ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>No Venues Yet</h3>
            <p>Create your first venue to start managing locations and tracking visits.</p>
            <button 
              className="btn-primary"
              onClick={() => setShowCreateVenue(true)}
            >
              Create Your First Venue
            </button>
          </div>
        </div>
      ) : (
        <div className="venues-list">
          {venues.map((venue) => {
            const venueLocations = getLocationsForVenue(venue.id);
            const isExpanded = expandedVenues.has(venue.id);
            
            return (
              <div key={venue.id} className="venue-card">
                <div className="venue-header">
                  <div className="venue-info">
                    <h3>{venue.name}</h3>
                    <div className="venue-id-display">
                      <span className="venue-id-label">Venue ID:</span>
                      <code className="venue-id-value">{venue.id}</code>
                      <button 
                        className={`btn-copy-id ${copiedVenueId === venue.id ? 'copied' : ''}`}
                        onClick={() => handleCopyVenueId(venue.id)}
                        title="Copy Venue ID"
                      >
                        {copiedVenueId === venue.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p className="venue-address">
                      {venue.city}{venue.state && `, ${venue.state}`}, {venue.country}
                    </p>
                    <p className="venue-meta">
                      {venueLocations.length} location{venueLocations.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="venue-actions">
                    <VenueActionsMenu
                      onEdit={() => handleEditVenue(venue)}
                      onDelete={() => handleDeleteVenue(venue)}
                      onAddLocation={() => setShowCreateLocation(venue.id)}
                      onToggleLocations={() => toggleVenueExpansion(venue.id)}
                      onToggleQR={() => toggleVenueQR(venue.id)}
                      isLocationsExpanded={isExpanded}
                      isQRExpanded={expandedQRVenues.has(venue.id)}
                    />
                  </div>
                </div>

                {/* QR Code Content for Venue */}
                {expandedQRVenues.has(venue.id) && (
                  <QRCodeContent
                    qrCodeSvgUrl={venue.qr_code_svg_url}
                    qrCodePngUrl={venue.qr_code_png_url}
                    onboardUrl={venue.onboard_url}
                    venueName={venue.name}
                    type="venue"
                  />
                )}

                {isExpanded && (
                  <div className="locations-section">
                    {venueLocations.length === 0 ? (
                      <div className="no-locations">
                        <p>No locations yet. Add your first location to start tracking visits.</p>
                        <button 
                          className="btn-primary"
                          onClick={() => setShowCreateLocation(venue.id)}
                        >
                          Add First Location
                        </button>
                      </div>
                    ) : (
                      <div className="locations-list">
                        {venueLocations.map((location) => (
                          <div key={location.id}>
                            <div className="location-item">
                              <div className="location-info">
                                <span className="location-label">{location.label}</span>
                                <span className="location-meta">
                                  Created {new Date(location.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="location-actions">
                                <LocationActionsMenu
                                  onEdit={() => handleEditLocation(location)}
                                  onDelete={() => handleDeleteLocation(location)}
                                  onToggleQR={() => toggleLocationQR(location.id)}
                                  isQRExpanded={expandedQRLocations.has(location.id)}
                                />
                              </div>
                            </div>
                            
                            {/* QR Code Content for Location */}
                            {expandedQRLocations.has(location.id) && (
                              <QRCodeContent
                                qrCodeSvgUrl={location.qr_code_svg_url}
                                qrCodePngUrl={location.qr_code_png_url}
                                onboardUrl={location.onboard_url}
                                venueName={venue.name}
                                locationLabel={location.label}
                                type="location"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Venue Modal */}
      {showCreateVenue && (
        <div className="modal-overlay" onClick={() => setShowCreateVenue(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Venue</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateVenue(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateVenue} className="modal-body">
              <div className="form-group">
                <label htmlFor="venue-name">Venue Name *</label>
                <input
                  type="text"
                  id="venue-name"
                  required
                  value={newVenue.name}
                  onChange={(e) => setNewVenue({...newVenue, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Downtown Restaurant"
                />
              </div>
              <div className="form-group">
                <label htmlFor="venue-city">City *</label>
                <input
                  type="text"
                  id="venue-city"
                  required
                  value={newVenue.city}
                  onChange={(e) => setNewVenue({...newVenue, city: e.target.value})}
                  className="form-input"
                  placeholder="e.g., New York"
                />
              </div>
              <div className="form-group">
                <label htmlFor="venue-state">State/Province</label>
                <input
                  type="text"
                  id="venue-state"
                  value={newVenue.state}
                  onChange={(e) => setNewVenue({...newVenue, state: e.target.value})}
                  className="form-input"
                  placeholder="e.g., NY"
                />
              </div>
              <div className="form-group">
                <label htmlFor="venue-country">Country *</label>
                <input
                  type="text"
                  id="venue-country"
                  required
                  value={newVenue.country}
                  onChange={(e) => setNewVenue({...newVenue, country: e.target.value})}
                  className="form-input"
                  placeholder="e.g., United States"
                />
              </div>
              <div className="form-group">
                <label htmlFor="venue-internal-id">Internal ID</label>
                <input
                  type="text"
                  id="venue-internal-id"
                  value={newVenue.internal_id || ''}
                  onChange={(e) => setNewVenue({...newVenue, internal_id: e.target.value})}
                  className="form-input"
                  placeholder="e.g., venue_12345"
                />
                <small className="form-help">
                  Optional. Your internal identifier for this venue (e.g., from your database).
                </small>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowCreateVenue(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Venue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Location Modal */}
      {showCreateLocation && (
        <div className="modal-overlay" onClick={() => setShowCreateLocation(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Location</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateLocation(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => handleCreateLocation(e, showCreateLocation)} className="modal-body">
              <div className="form-group">
                <label htmlFor="location-label">Location Label *</label>
                <input
                  type="text"
                  id="location-label"
                  required
                  value={newLocation.label}
                  onChange={(e) => setNewLocation({...newLocation, label: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Main Entrance, Table 5, Bar Area"
                />
                <small>This will be normalized to lowercase for consistency</small>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowCreateLocation(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Venue Modal */}
      {showEditVenue && (
        <div className="modal-overlay" onClick={() => setShowEditVenue(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Venue</h2>
              <button 
                className="modal-close"
                onClick={() => setShowEditVenue(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateVenue} className="modal-body">
              <div className="form-group">
                <label htmlFor="edit-venue-name">Venue Name *</label>
                <input
                  type="text"
                  id="edit-venue-name"
                  required
                  value={editVenue.name}
                  onChange={(e) => setEditVenue({...editVenue, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Downtown Restaurant"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-venue-city">City *</label>
                <input
                  type="text"
                  id="edit-venue-city"
                  required
                  value={editVenue.city}
                  onChange={(e) => setEditVenue({...editVenue, city: e.target.value})}
                  className="form-input"
                  placeholder="e.g., New York"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-venue-state">State/Province</label>
                <input
                  type="text"
                  id="edit-venue-state"
                  value={editVenue.state}
                  onChange={(e) => setEditVenue({...editVenue, state: e.target.value})}
                  className="form-input"
                  placeholder="e.g., NY"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-venue-country">Country *</label>
                <input
                  type="text"
                  id="edit-venue-country"
                  required
                  value={editVenue.country}
                  onChange={(e) => setEditVenue({...editVenue, country: e.target.value})}
                  className="form-input"
                  placeholder="e.g., United States"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-venue-internal-id">Internal ID</label>
                <input
                  type="text"
                  id="edit-venue-internal-id"
                  value={editVenue.internal_id || ''}
                  onChange={(e) => setEditVenue({...editVenue, internal_id: e.target.value})}
                  className="form-input"
                  placeholder="e.g., venue_12345"
                />
                <small className="form-help">
                  Optional. Your internal identifier for this venue (e.g., from your database).
                </small>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowEditVenue(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update Venue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Location Modal */}
      {showEditLocation && (
        <div className="modal-overlay" onClick={() => setShowEditLocation(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Location</h2>
              <button 
                className="modal-close"
                onClick={() => setShowEditLocation(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateLocation} className="modal-body">
              <div className="form-group">
                <label htmlFor="edit-location-label">Location Label *</label>
                <input
                  type="text"
                  id="edit-location-label"
                  required
                  value={editLocation.label}
                  onChange={(e) => setEditLocation({...editLocation, label: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Main Entrance, Table 5, Bar Area"
                />
                <small>This will be normalized to lowercase for consistency</small>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowEditLocation(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Venue Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!showDeleteVenueDialog}
        title="Delete Venue"
        message={`Are you sure you want to delete "${showDeleteVenueDialog?.name}"? This will also delete all associated locations and cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteVenueConfirm}
        onCancel={handleDeleteVenueCancel}
        type="danger"
      />

      {/* Delete Location Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!showDeleteLocationDialog}
        title="Delete Location"
        message={`Are you sure you want to delete "${showDeleteLocationDialog?.label}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteLocationConfirm}
        onCancel={handleDeleteLocationCancel}
        type="danger"
      />

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="modal-overlay" onClick={handleCSVClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Venues CSV</h3>
              <button 
                className="modal-close"
                onClick={handleCSVClose}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="csv-file">Select CSV File</label>
                <input
                  type="file"
                  id="csv-file"
                  accept=".csv"
                  onChange={handleCSVFileSelect}
                  className="form-input"
                />
                <small className="form-help">
                  CSV should have columns: name, city, state, country, internal_id
                </small>
              </div>
              
              <div className="form-group">
                <label>Action to perform:</label>
                <div className="csv-upload-container">
                  <label className="csv-upload-option">
                    <input
                      type="radio"
                      name="csvAction"
                      value="add"
                      checked={csvAction === 'add'}
                      onChange={(e) => setCsvAction(e.target.value as 'add')}
                      className="csv-upload-radio"
                    />
                    <div className="csv-upload-content">
                      <div className="csv-upload-title">Add new venues</div>
                      <div className="csv-upload-description">
                        Keep existing venues and add new ones from CSV
                      </div>
                    </div>
                  </label>
                  <label className="csv-upload-option">
                    <input
                      type="radio"
                      name="csvAction"
                      value="replace"
                      checked={csvAction === 'replace'}
                      onChange={(e) => setCsvAction(e.target.value as 'replace')}
                      className="csv-upload-radio"
                    />
                    <div className="csv-upload-content">
                      <div className="csv-upload-title">Replace all venues</div>
                      <div className="csv-upload-description">
                        Delete all existing venues, then create new ones from CSV
                      </div>
                    </div>
                  </label>
                  <label className="csv-upload-option">
                    <input
                      type="radio"
                      name="csvAction"
                      value="delete"
                      checked={csvAction === 'delete'}
                      onChange={(e) => setCsvAction(e.target.value as 'delete')}
                      className="csv-upload-radio"
                    />
                    <div className="csv-upload-content">
                      <div className="csv-upload-title">Delete venues</div>
                      <div className="csv-upload-description">
                        Delete venues that match the internal_id values in CSV
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              {csvProgress && (
                <div className="csv-progress-container">
                  <div className="csv-progress-bar">
                    <div 
                      className="csv-progress-fill"
                      style={{ 
                        width: `${(csvProgress.current / csvProgress.total) * 100}%` 
                      }}
                    />
                  </div>
                  <div className="csv-progress-text">
                    Processing {csvProgress.current} of {csvProgress.total} venues...
                  </div>
                </div>
              )}
              
              {csvSuccess && (
                <div className="form-success">
                  {csvSuccess}
                </div>
              )}
              
              {csvError && (
                <div className="form-error">
                  {csvError}
                </div>
              )}
              
              {csvData.length > 0 && (
                <div className="csv-preview-container">
                  <h4>
                    {csvAction === 'add' && `Preview - Adding ${csvData.length} venues`}
                    {csvAction === 'replace' && `Preview - Replacing all venues with ${csvData.length} new venues`}
                    {csvAction === 'delete' && `Preview - Deleting ${csvData.length} venues`}
                  </h4>
                  <div className="csv-preview-table">
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(csvData[0] || {}).map(header => (
                            <th key={header}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td key={i}>
                                {String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvData.length > 5 && (
                      <div className="csv-preview-more">
                        ... and {csvData.length - 5} more rows
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={handleCSVClose}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-primary"
                disabled={csvData.length === 0 || csvProcessing}
                onClick={handleBulkVenueAction}
              >
                {csvProcessing ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" />
                    Processing {csvProgress?.current || 0} of {csvProgress?.total || csvData.length}...
                  </span>
                ) : (
                  csvAction === 'add' ? `Add ${csvData.length} Venues` :
                  csvAction === 'replace' ? `Replace All with ${csvData.length} Venues` :
                  `Delete ${csvData.length} Venues`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showReplaceConfirm}
        title="Replace All Venues"
        message={`This will delete all ${venues.length} existing venues and create ${csvData.length} new venues. This action cannot be undone. Are you sure?`}
        confirmText="Yes, Replace All"
        cancelText="Cancel"
        onConfirm={() => {
          setShowReplaceConfirm(false);
          executeBulkAction();
        }}
        onCancel={() => setShowReplaceConfirm(false)}
        type="danger"
      />

      {/* Toast notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
        </>
      )}
    </div>
  );
};

export default Venues;
